import { CheckCheck, Download, ListFilter, Lock, Send, ShieldCheck, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { PremiumBadge } from '~/components/common/premium-badge';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type {
  BatchPipelineStage,
  BatchPipelineStageKey,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

const STAGE_ICON: Record<BatchPipelineStageKey, LucideIcon> = {
  received: Download,
  curated: ListFilter,
  enriched: Sparkles,
  validated: CheckCheck,
  delivered: Send,
  verified: ShieldCheck,
};

/** CSS class applied to the icon wrapper span — drives the keyframe selector
 *  in utilities.css. Matched to the semantic meaning of each stage verb. */
const ICON_ANIM: Record<BatchPipelineStageKey, string> = {
  received: 'ico-bob', // download arrow bobs down — data arriving
  curated: 'ico-stagger', // filter bars stagger-fade — sorting through
  enriched: 'ico-twinkle', // sparkles scale + fade — adding value
  validated: 'ico-stagger', // check marks stagger — verifying
  delivered: 'ico-nudge', // paper plane nudges up-right — flying off
  verified: 'ico-bob', // shield bobs — checking integrity
};

const CARD_CLASS = {
  complete: 'border-success bg-success-50',
  active: 'border-stage-active bg-stage-active-bg',
  pending: 'border-neutral-200 bg-white',
  failed: 'border-error bg-error-50',
  // Terminal but not a fault — a dashed border reads as "this never ran"
  // without borrowing the error palette. Red stays reserved for `failed`.
  skipped: 'border-dashed border-neutral-300 bg-neutral-50',
  // An operator decision, not a system error: distinct from both the neutral
  // skip and the red failure.
  rejected: 'border-warning bg-warning-50',
};

const ICON_CLASS = {
  complete: 'bg-success text-white',
  active: 'bg-stage-active text-white',
  pending: 'bg-neutral-200 text-neutral-500',
  failed: 'bg-error text-white',
  skipped: 'bg-neutral-200 text-neutral-400',
  rejected: 'bg-warning text-white',
};

// The connector renders after each card and visually represents the flow
// continuing onward. `failed` here means the connector leads INTO a failed
// stage, so it should read as red like the card it feeds. The connector
// AFTER a failed stage is a separate case — `connectorState` below forces
// that one to `pending` since the flow has halted and there's nothing
// flowing onward to highlight.
const CONNECTOR_CLASS = {
  complete: 'bg-success',
  active: 'bg-stage-active',
  pending: 'bg-neutral-200',
  failed: 'bg-error',
  skipped: 'bg-neutral-200',
  rejected: 'bg-warning',
};

/** Batch retry note under the Enriched count, e.g. "324 not enriched yet ›". */
export interface EnrichedNote {
  text: string;
  tone: 'warning' | 'muted';
  /** Set = the note is a button (opens the Needs Attention drawer). */
  onClick?: () => void;
  /** Full sentence for the tooltip when {@link text} is a short form. */
  title?: string;
}

interface Props {
  stages: BatchPipelineStage[];
  /** Batch retry note rendered under the Enriched card's value. */
  enrichedNote?: EnrichedNote;
  /** Carry-over link under the Enriched card ("21 of 26 from All Products · 26 Oct ›"); a second
   *  line, so it never replaces the retry note above. */
  carryoverNote?: EnrichedNote;
  /** Small hint on the Delivery card, e.g. "approve without 324". */
  deliveryHint?: string;
  /** True when the batch is paused at AWAITING_APPROVAL — swaps the Delivery
   *  stage's "Scheduled" pending pill for a small review button. Stays true
   *  while delivering (top-level status doesn't change), so `deliveryInProgress`
   *  is what actually disables the Review action. */
  awaitingApproval?: boolean;
  /** True when the decoupled delivery is actively running
   *  (`pipelineStatus === 'DELIVERING'`) — the authoritative signal that the
   *  Review action is no longer available and must render disabled. */
  deliveryInProgress?: boolean;
  /** True for trial batches paused at AWAITING_APPROVAL — trial batches never
   *  reach a real delivery, so the Delivery stage renders locked (lock icon +
   *  premium badge) and its Review button is disabled instead of actionable. */
  deliveryLocked?: boolean;
  /** Opens the delivery approval modal. */
  onReviewDelivery?: () => void;
  /** Opens the workflow-retry confirm dialog for the failed stage. */
  onRetry?: () => void;
  /** True once discovery has resolved a failure point to retry from —
   *  the Retry button stays disabled (with a loading spinner) until then. */
  retryReady?: boolean;
  /** True while the failure-points discovery query is in flight. */
  retryLoading?: boolean;
  /** Navigates to this batch's newly-enriched products. Wired to the Enriched
   *  stage card, which becomes clickable only when it has a non-zero count. */
  onEnrichedClick?: () => void;
  /** Opens the pre/enriched/delivered compare view. Shown as a small button on
   *  the Delivered card once it's complete; omitted (button hidden) when the
   *  `compare-delivery` gate is off or a delivery job id isn't known. */
  onCompare?: () => void;
}

/** Tense-specific labels per stage key. The backend sends the same label
 *  regardless of progress state, so the frontend owns all tense transforms. */
const STAGE_LABEL: Record<BatchPipelineStageKey, Record<BatchPipelineStage['state'], string>> = {
  received: {
    pending: 'Receive',
    active: 'Receiving',
    complete: 'Received',
    failed: 'Receive',
    skipped: 'Not received',
    rejected: 'Receipt declined',
  },
  curated: {
    pending: 'Curation',
    active: 'Curating',
    complete: 'Curated',
    failed: 'Curation',
    skipped: 'Not curated',
    rejected: 'Curation declined',
  },
  enriched: {
    pending: 'Enrichment',
    active: 'Enriching',
    complete: 'Enriched',
    failed: 'Enrichment',
    skipped: 'Not enriched',
    rejected: 'Enrichment declined',
  },
  validated: {
    pending: 'Validate',
    active: 'Validating',
    complete: 'Validated',
    failed: 'Validate',
    skipped: 'Not validated',
    rejected: 'Validation declined',
  },
  delivered: {
    pending: 'Delivery',
    active: 'Delivering',
    complete: 'Delivered',
    failed: 'Delivery',
    // Unreachable in practice — `haltedBySkip` below is unconditionally true
    // whenever the delivery stage is `skipped` (2026-09-30), so `stageLabel`
    // never reads this entry for `delivered`. Kept only because `STAGE_LABEL`
    // is a full `Record` and every stage needs every state key.
    skipped: 'Not delivered',
    rejected: 'Delivery declined',
  },
  verified: {
    pending: 'Verify',
    active: 'Verifying',
    complete: 'Verified',
    failed: 'Verify',
    skipped: 'Not verified',
    rejected: 'Verification declined',
  },
};

/** Why a stage will never run, shown as a tooltip on terminal non-failure
 *  states. Keyed to the stage the operator is most likely to question.
 *  `delivered` has no entry — it always renders the halted "Skipped"
 *  treatment (see `haltedBySkip` below), which suppresses this hint. */
const SKIPPED_HINT: Partial<Record<BatchPipelineStageKey, string>> = {};

const REJECTED_HINT = 'An operator declined this run at the approval gate.';

/** True when an earlier stage in the strip failed or was rejected — meaning
 *  this stage's `skipped` state means "the pipeline halted before ever
 *  reaching it", not "the run completed clean and genuinely had nothing to
 *  push" (the scenario the stage-specific "Not X" copy below is written for —
 *  see docs/pipeline-stage-terminal-states.md). Only the halted case reads as
 *  a plain "Skipped". */
function precededByFailure(stages: BatchPipelineStage[], index: number): boolean {
  return stages.slice(0, index).some((s) => s.state === 'failed' || s.state === 'rejected');
}

/** Returns the tense-appropriate label for a stage based on its current state.
 *  A stage rendering the halted "Skipped" treatment (an upstream failure, or
 *  the delivery stage itself) keeps its plain, neutral name (e.g. "Delivery")
 *  — the verdict itself ("Skipped") renders in the value slot instead, not
 *  folded into the title (see the card's value rendering below). */
function stageLabel(stage: BatchPipelineStage, haltedBySkip: boolean): string {
  if (haltedBySkip) return STAGE_LABEL[stage.key].pending;
  return STAGE_LABEL[stage.key][stage.state];
}

/** Returns the visual state for the connector drawn between stage[i] and stage[i+1].
 *  The connector adopts the state of the stage it leads INTO — unless the
 *  preceding stage has failed, in which case flow is halted (pending/muted). */
function connectorState(
  prevState: BatchPipelineStage['state'],
  nextState: BatchPipelineStage['state'],
): BatchPipelineStage['state'] {
  // `rejected` halts the flow for the same reason `failed` does — the run
  // stopped here and nothing is moving onward.
  if (prevState === 'failed' || prevState === 'rejected') return 'pending';
  return nextState;
}

export function BatchPipelineStages({
  stages,
  enrichedNote,
  carryoverNote,
  deliveryHint,
  awaitingApproval,
  deliveryInProgress,
  deliveryLocked,
  onReviewDelivery,
  onRetry,
  retryReady,
  retryLoading,
  onEnrichedClick,
  onCompare,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div data-testid="batch-detail-pipeline-stages" className="flex items-stretch overflow-x-auto">
      {stages.map((stage, i) => {
        // State is driven entirely by the backend status — scheduled → gray,
        // in_progress → orange, complete → green, failed → red.
        const effectiveState = stage.state;
        const isLockedDelivery = stage.key === 'delivered' && !!deliveryLocked;
        // While the delivery stage is actively pushing to the destination
        // ("Delivering"), the batch can no longer be reviewed/approved, so the
        // Review button reads disabled rather than actionable. The authoritative
        // signal is `deliveryInProgress` (from `pipelineStatus`); fall back to the
        // delivered stage's active state for rollout-era rows that lack it.
        const isDelivering =
          stage.key === 'delivered' && (deliveryInProgress === true || effectiveState === 'active');
        const Icon = isLockedDelivery ? Lock : STAGE_ICON[stage.key];
        const iconAnimClass = isLockedDelivery ? '' : ICON_ANIM[stage.key];
        const isLast = i === stages.length - 1;
        const nextStage = stages[i + 1];
        // The Enriched card drills into this batch's newly-enriched products —
        // but only once enrichment has produced any (a zero count has nothing to
        // show). A failed stage renders its own Retry button, so it never becomes
        // clickable (that would nest interactive controls).
        // A `skipped` stage after a failed/rejected one never got a chance to
        // run — the red card's Retry action already explains why, so it needs
        // no hint of its own (and no "Not X" euphemism; see `stageLabel`).
        // Delivery is the same either way (2026-09-30): whether it never ran
        // because an earlier stage failed, or the run completed clean and
        // simply never pushed a feed, both read as a plain "Skipped" verdict —
        // "Not delivered" implied a fault-free non-event that isn't a
        // meaningful distinction for delivery specifically.
        const haltedBySkip =
          effectiveState === 'skipped' &&
          (stage.key === 'delivered' || precededByFailure(stages, i));
        // "Why did this never run" copy for terminal non-failure states. Failure
        // is already explained by the red card and its Retry action.
        const terminalHint =
          effectiveState === 'skipped'
            ? haltedBySkip
              ? undefined
              : SKIPPED_HINT[stage.key]
            : effectiveState === 'rejected'
              ? REJECTED_HINT
              : undefined;
        const isEnrichedClickable =
          stage.key === 'enriched' &&
          effectiveState !== 'failed' &&
          (stage.count ?? 0) > 0 &&
          !!onEnrichedClick;
        return (
          <Fragment key={`${stage.key}-${String(i)}`}>
            {/* Stage card — `stage` class is the CSS keyframe hook, `data-state`
                drives all animation and color rules declaratively. */}
            <div
              data-testid={`batch-detail-stage-${stage.key}`}
              data-state={effectiveState}
              aria-current={effectiveState === 'active' ? 'step' : undefined}
              role={isEnrichedClickable ? 'button' : undefined}
              tabIndex={isEnrichedClickable ? 0 : undefined}
              aria-label={isEnrichedClickable ? 'View newly enriched products' : undefined}
              onClick={isEnrichedClickable ? onEnrichedClick : undefined}
              onKeyDown={
                isEnrichedClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEnrichedClick();
                      }
                    }
                  : undefined
              }
              className={cn(
                'stage flex min-w-[200px] flex-1 items-center gap-3.5 rounded-[18px]',
                'border-2 py-3.5 pr-6 pl-3.5 transition-colors duration-500',
                // The halted "Skipped" treatment reads as a muted, unstyled
                // non-event rather than the dashed "terminal but not a fault"
                // treatment — for an upstream failure, the fault already lives
                // on the failed card earlier in the strip; for delivery, it's
                // simply the neutral way this stage always reads when skipped.
                haltedBySkip ? 'bg-muted border-neutral-200' : CARD_CLASS[effectiveState],
                isEnrichedClickable &&
                  'focus-visible:ring-success cursor-pointer hover:shadow-md focus-visible:ring-2 focus-visible:outline-none',
              )}
            >
              {/* Icon tile */}
              <div
                className={cn(
                  'flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl',
                  'transition-colors duration-500',
                  haltedBySkip ? 'bg-background text-muted-foreground' : ICON_CLASS[effectiveState],
                )}
              >
                {/* Icon wrapper span — class name selected per stage key;
                    CSS keyframes in utilities.css target this selector. */}
                <span className={iconAnimClass}>
                  <Icon className="h-5 w-5" aria-hidden="true" strokeWidth={2.25} />
                </span>
              </div>

              {/* Label + value — text color is constant across all states per spec */}
              <div className="min-w-0">
                <p
                  data-testid={`batch-detail-stage-label-${stage.key}`}
                  className="text-stage-text flex items-center gap-1 truncate text-sm leading-tight"
                >
                  {/* Terminal non-failure states carry a "why" tooltip — on their
                      own the labels ("Not delivered") state the outcome but not
                      the reason. Other states render the plain label. */}
                  {terminalHint ? (
                    <Tooltip delayDuration={150}>
                      <TooltipTrigger asChild>
                        <span className="cursor-help truncate underline decoration-dotted underline-offset-4">
                          {stageLabel(stage, haltedBySkip)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64 text-center">
                        {terminalHint}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="truncate">{stageLabel(stage, haltedBySkip)}</span>
                  )}
                  {isLockedDelivery && <PremiumBadge size={14} />}
                </p>
                {stage.key === 'delivered' && awaitingApproval ? (
                  <Button
                    data-testid="batch-detail-stage-review-button"
                    variant="outline"
                    size="sm"
                    className="border-primary text-primary hover:text-primary mt-1 h-6 cursor-pointer px-2 text-xs disabled:cursor-not-allowed"
                    onClick={onReviewDelivery}
                    disabled={isLockedDelivery || isDelivering}
                  >
                    Review
                  </Button>
                ) : effectiveState === 'failed' ? (
                  <Button
                    data-testid={`batch-detail-stage-retry-button-${stage.key}`}
                    variant="outline"
                    size="sm"
                    className="border-error text-error hover:text-error mt-1 h-6 cursor-pointer px-2 text-xs disabled:cursor-not-allowed"
                    onClick={onRetry}
                    isLoading={!!retryLoading}
                    disabled={!retryReady}
                  >
                    {t('retry.button')}
                  </Button>
                ) : effectiveState === 'pending' && stage.pendingLabel ? (
                  <span
                    data-testid={`batch-detail-stage-pending-badge-${stage.key}`}
                    className="mt-1 inline-block rounded bg-white px-1.5 py-0.5 text-xs font-medium text-neutral-600"
                  >
                    {stage.pendingLabel}
                  </span>
                ) : haltedBySkip ? (
                  // The verdict lives here, in the value slot — the title stays
                  // the stage's plain name (see `stageLabel`).
                  <p
                    data-testid={`batch-detail-stage-value-${stage.key}`}
                    className="text-stage-text mt-0.5 text-[22px] font-bold"
                  >
                    Skipped
                  </p>
                ) : stage.key === 'delivered' && effectiveState === 'complete' && onCompare ? (
                  // Count and the Compare button share one row (not stacked)
                  // so this card stays the same height as its siblings.
                  <div className="flex items-center gap-2">
                    <p
                      data-testid={`batch-detail-stage-value-${stage.key}`}
                      className="text-stage-text text-[22px] font-bold tabular-nums"
                    >
                      {stage.count === null ? '—' : stage.count.toLocaleString()}
                    </p>
                    <Button
                      data-testid="batch-detail-stage-compare-button"
                      variant="outline"
                      size="sm"
                      className="border-success text-success hover:text-success h-6 cursor-pointer px-2 text-xs"
                      onClick={onCompare}
                    >
                      {t('compare.button')}
                    </Button>
                  </div>
                ) : (
                  <p
                    data-testid={`batch-detail-stage-value-${stage.key}`}
                    className="text-stage-text mt-0.5 text-[22px] font-bold tabular-nums"
                  >
                    {/* `null` is "not known yet", `0` is "genuinely none" — they
                        must not render the same. */}
                    {stage.count === null ? '—' : stage.count.toLocaleString()}
                  </p>
                )}
                {stage.key === 'enriched' && enrichedNote && (
                  <EnrichedNoteLine note={enrichedNote} />
                )}
                {stage.key === 'enriched' && carryoverNote && (
                  <EnrichedNoteLine
                    note={carryoverNote}
                    testId="batch-detail-stage-carryover-note"
                  />
                )}
                {stage.key === 'delivered' && deliveryHint && (
                  <p
                    data-testid="batch-detail-stage-delivery-hint"
                    className="text-muted-foreground mt-1 text-xs font-medium"
                  >
                    {deliveryHint}
                  </p>
                )}
              </div>
            </div>

            {/* Connector — `connector` class is the CSS keyframe hook.
                State is derived from the stage it leads INTO (not the stage
                it comes from), so active connectors carry the flowing dots
                that visually pull data forward. The post-failure connector
                goes muted to signal that flow has halted. */}
            {!isLast && nextStage && (
              <div
                aria-hidden="true"
                data-state={connectorState(effectiveState, nextStage.state)}
                className={cn(
                  'connector relative h-0.5 w-7 shrink-0 self-center transition-colors duration-500',
                  CONNECTOR_CLASS[connectorState(effectiveState, nextStage.state)],
                )}
              >
                <span className="flow-dot" />
                <span className="flow-dot" />
                <span className="flow-dot" />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function EnrichedNoteLine({
  note,
  testId = 'batch-detail-stage-enriched-note',
}: {
  note: EnrichedNote;
  testId?: string;
}) {
  // One line, left-aligned with the value above; a long note truncates (full text on hover).
  const className = cn(
    'mt-0.5 block max-w-full truncate text-left text-xs font-medium',
    note.tone === 'warning' ? 'text-orange-700' : 'text-muted-foreground',
  );
  if (!note.onClick) {
    return (
      <span data-testid={testId} className={className} title={note.title}>
        {note.text}
      </span>
    );
  }
  const onClick = note.onClick;
  return (
    <button
      type="button"
      data-testid={testId}
      title={note.title}
      className={cn(className, 'cursor-pointer hover:underline')}
      onClick={(e) => {
        // The card itself drills into enriched products; this note opens Needs Attention.
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
    >
      {note.text} ›
    </button>
  );
}
